import type { TurnSnapshot, FinalizedSession } from './AudioRecorder';

export interface TurnRuntimeMeta {
  rnnoiseEnabled: boolean;
  reconnectCount: number;
  micSampleRate?: number;
}

export interface SessionStartMeta {
  sessionId: string;
  userId?: string;
  userEmail?: string;
  startTime: number;
  systemPrompt?: string;
  provider?: string;
  posthogRecordingUrl?: string;
  runtimeMeta?: TurnRuntimeMeta;
  contextType?: string;
  elementId?: string;
  skillId?: string;
}

async function postWithTimeout(url: string, body: BodyInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => {
    controller.abort(new DOMException(`Request timed out after ${timeoutMs}ms`, 'TimeoutError'));
  }, timeoutMs);
  try {
    return await fetch(url, { method: 'POST', body, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

function isAbortLikeError(error: unknown): error is DOMException {
  return error instanceof DOMException && (
    error.name === 'AbortError' || error.name === 'TimeoutError'
  );
}

/** Create/start live-session trace at session setup time */
export async function uploadSessionStart(meta: SessionStartMeta): Promise<void> {
  try {
    const res = await fetch('/napi/live-session/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(meta),
    });
    if (!res.ok) {
      console.error('[Upload] Session start failed:', res.status, await res.text());
    }
  } catch (err) {
    console.error('[Upload] Session start network error:', err);
  }
}

/** Upload turn snapshot (cumulative audio + transcription) */
export async function uploadTurnSnapshot(
  sessionId: string,
  userId: string | undefined,
  startTime: number,
  snapshot: TurnSnapshot,
  runtimeMeta?: TurnRuntimeMeta,
): Promise<void> {
  const form = new FormData();

  const turnMeta = {
    sessionId,
    userId,
    startTime,
    roundIndex: snapshot.roundIndex,
    rounds: snapshot.rounds,
    runtimeMeta,
  };
  form.append('turnMeta', JSON.stringify(turnMeta));
  form.append('sessionAudio', snapshot.sessionAudioWav, 'session.wav');

  try {
    const res = await fetch('/napi/live-session/turn', { method: 'POST', body: form });
    if (!res.ok) {
      console.error('[Upload] Turn snapshot failed:', res.status, await res.text());
    }
  } catch (err) {
    console.error('[Upload] Turn snapshot network error:', err);
  }
}

/** Upload finalized session data */
export async function uploadSessionEnd(data: FinalizedSession): Promise<void> {
  const createForm = () => {
    const form = new FormData();
    const metadata = {
      sessionId: data.sessionId,
      userId: data.userId,
      userEmail: data.userEmail,
      startTime: data.startTime,
      endTime: data.endTime,
      duration: data.duration,
      totalRounds: data.totalRounds,
      rounds: data.rounds,
      systemPrompt: data.systemPrompt,
      provider: data.provider,
      posthogRecordingUrl: data.posthogRecordingUrl,
    };
    form.append('metadata', JSON.stringify(metadata));
    form.append('sessionAudio', data.fullSessionWav, 'session.wav');
    return form;
  };

  try {
    let res = await postWithTimeout('/napi/live-session/end', createForm(), 7000);
    if (!res.ok) {
      console.error('[Upload] Session end failed (attempt 1):', res.status, await res.text());
      res = await postWithTimeout('/napi/live-session/end', createForm(), 7000);
      if (!res.ok) {
        console.error('[Upload] Session end failed (attempt 2):', res.status, await res.text());
      }
    }
  } catch (err) {
    if (isAbortLikeError(err)) {
      console.warn('[Upload] Session end upload timed out:', err.message || err.name);
    } else {
      console.error('[Upload] Session end network/timeout error:', err);
    }
    try {
      const retryRes = await postWithTimeout('/napi/live-session/end', createForm(), 7000);
      if (!retryRes.ok) {
        console.error('[Upload] Session end retry failed:', retryRes.status, await retryRes.text());
      }
    } catch (retryErr) {
      if (isAbortLikeError(retryErr)) {
        console.warn('[Upload] Session end retry timed out:', retryErr.message || retryErr.name);
      } else {
        console.error('[Upload] Session end retry network/timeout error:', retryErr);
      }
    }
  }
}

/** Lightweight beacon for abnormal exit (beforeunload) */
export function sendSessionBeacon(sessionId: string, userId: string | undefined, startTime: number): void {
  const payload = JSON.stringify({
    sessionId,
    userId,
    startTime,
    abnormalExit: true,
    timestamp: Date.now(),
  });

  navigator.sendBeacon(
    '/napi/live-session/end',
    new Blob([payload], { type: 'application/json' }),
  );
}
